import { useMemo, forwardRef, useImperativeHandle, useRef, useEffect, useState } from 'react';
import FJLocalStore from '../../../../store/FJLocalStore';
import styles from './FJAnimationSet.module.less';
import DownIcon from '../../../../assets/svg/down.svg?react';
import HorizontalIcon from '../../../../assets/svg/horizontal.svg?react';
import UpIcon from '../../../../assets/svg/up.svg?react';
import VerticalIcon from '../../../../assets/svg/vertical.svg?react';
import CenterIcon from '../../../../assets/svg/center.svg?react';
import ShadowArrowIcon from '../../../../assets/svg/shadow-arrow.svg?react';
import FJSlider from '../../../../common/components/FJSlider/FJSlider';
import FJSwitchButton from '../../../../common/components/FJSwitchButton/FJSwitchButton';
import classNames from 'classnames';
import FJValueInput from '../../../../common/components/FJValueInput/FJValueInput';
import PropTypes from 'prop-types';

const FJAnimationSet = (props, ref) => {
    const {
        directions = [],
        direction,
        intensity,
        duration,
        returnAnimation,
        reverse,
        onDirectionChange,
        onIntensityChange,
        onDurationChange,
        onReturnAnimationChange,
        onReverseChange,
        arrowPositionClassName,
    } = props;
    const containerRef = useRef(null);
    const [sliderDuration, setSliderDuration] = useState(0);
    useImperativeHandle(ref, () => ({
        getContainer: () => {
            return containerRef.current;
        },
    }));
    const _onDirectionChange = type => {
        onDirectionChange(type);
    };
    const _onIntensityChange = (value, mouseType) => {
        onIntensityChange(Math.ceil(value), mouseType !== 'mousemove');
    };
    const _onDurationChange = (value, mouseType) => {
        onDurationChange(Math.ceil(value * 10) / 10, mouseType !== 'mousemove');
    };
    const _onSliderDurationChange = (value, mouseType) => {
        const dur = Math.ceil(value * 10 * (returnAnimation + 1)) / 10;
        onDurationChange(Math.ceil(dur * 10) / 10, mouseType !== 'mousemove');
    };
    const _onReturnAnimationChange = value => {
        onReturnAnimationChange(value);
        if (value) {
            onDurationChange(Math.ceil(duration * 2 * 10) / 10);
        } else {
            onDurationChange(Math.ceil((duration / 2) * 10) / 10);
        }
    };
    const _onReverseChange = value => {
        onReverseChange(value);
    };
    const geyIconByType = type => {
        switch (type) {
            case 'HORIZONTAL':
                return <HorizontalIcon />;
            case 'VERTICAL':
                return <VerticalIcon />;
            case 'UP':
                return <UpIcon />;
            case 'DOWN':
                return <DownIcon />;
            case 'CENTER':
                return <CenterIcon />;
        }
        return null;
    };
    const maxDuration = useMemo(() => {
        const Max = 10;
        return returnAnimation ? Max * 2 : Max;
    }, [returnAnimation]);

    useEffect(() => {
        const dur = returnAnimation ? duration / 2 : duration;
        setSliderDuration(dur);
    }, [duration, returnAnimation]);
    return (
        <div className={styles.setting_box} ref={containerRef}>
            {directions.length > 1 && (
                <>
                    <div className={styles.setting_title}>{FJLocalStore._('direction')}</div>
                    <div className={styles.direction_list}>
                        {directions.map(item => (
                            <div
                                onClick={() => {
                                    _onDirectionChange(item.type);
                                }}
                                key={item.type}
                                className={classNames(styles.setting_item, item.type === direction && styles.active)}
                            >
                                {geyIconByType(item.imageType)}
                            </div>
                        ))}
                    </div>
                </>
            )}
            <div className={styles.setting_slider_box}>
                <div className={styles.setting_slider_header}>
                    <div className={styles.setting_title}>{FJLocalStore._('intensity')}</div>
                    <FJValueInput
                        className={styles.input}
                        min={0}
                        max={100}
                        changeValue={_onIntensityChange}
                        value={intensity}
                    />
                </div>
                <div className={styles.setting_slider}>
                    <FJSlider
                        className={styles.slider}
                        max={100}
                        min={0}
                        value={intensity}
                        onChange={_onIntensityChange}
                    />
                </div>
            </div>
            <div className={styles.setting_slider_box}>
                <div className={styles.setting_slider_header}>
                    <div className={styles.setting_title}>{FJLocalStore._('duration')}</div>
                    <FJValueInput
                        className={styles.input}
                        min={1}
                        max={maxDuration}
                        changeValue={_onDurationChange}
                        value={duration}
                    />
                </div>
                <div className={styles.setting_slider}>
                    <FJSlider
                        className={styles.slider}
                        min={1}
                        max={10}
                        value={sliderDuration}
                        onChange={_onSliderDurationChange}
                    />
                </div>
            </div>
            <div className={styles.setting_switch}>
                <span className={styles.setting_title}>{FJLocalStore._('return')}</span>

                <FJSwitchButton active={returnAnimation} onChange={_onReturnAnimationChange} />
            </div>
            <div className={styles.setting_switch}>
                <span className={styles.setting_title}>{FJLocalStore._('reverse')}</span>
                <FJSwitchButton active={reverse} onChange={_onReverseChange} />
            </div>
            <ShadowArrowIcon className={classNames(styles.shadow_arrow, styles[arrowPositionClassName])} />
        </div>
    );
};

FJAnimationSet.propTypes = {
    arrowPositionClassName: PropTypes.string,
    direction: PropTypes.string,
    directions: PropTypes.array,
    intensity: PropTypes.number,
    duration: PropTypes.number,
    returnAnimation: PropTypes.bool,
    reverse: PropTypes.bool,
    onDirectionChange: PropTypes.func,
    onIntensityChange: PropTypes.func,
    onDurationChange: PropTypes.func,
    onReturnAnimationChange: PropTypes.func,
    onReverseChange: PropTypes.func,
};

export default forwardRef(FJAnimationSet);
